//
// Copyright © 2026 ZeroDesk
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Person } from '@hcengineering/contact'
import { type Ref } from '@hcengineering/core'
import type { Department } from './index'
import { PersonnelStatus, type PersonnelFile } from './personnel'

/**
 * Node of the organization chart. Either a department or a person (reporting line).
 */
export interface OrgNode {
  id: string
  kind: 'department' | 'person'
  title: string
  subtitle?: string
  // Team lead of a department, or the person of a reporting-line node
  person?: Person
  // Shown as "no team lead" when a department has no lead
  leadMissing?: boolean
  members?: Person[]
  memberCount?: number
  muted?: boolean
  children: OrgNode[]
  department?: Department
  file?: PersonnelFile
}

export interface DepartmentInput {
  department: Department
  lead?: Person
}

export interface StaffInput {
  person: Person
  department: Ref<Department> | undefined
}

/**
 * Department tree below the root department. Departments without a known parent hang directly below the root.
 */
export function buildDepartmentTree (
  rootId: Ref<Department>,
  rootTitle: string,
  departments: DepartmentInput[],
  staff: StaffInput[]
): OrgNode[] {
  const byId = new Map(departments.map((d) => [d.department._id, d]))
  const membersOf = new Map<Ref<Department>, Person[]>()
  for (const s of staff) {
    if (s.department === undefined) continue
    const list = membersOf.get(s.department) ?? []
    list.push(s.person)
    membersOf.set(s.department, list)
  }

  const childrenOf = new Map<Ref<Department>, DepartmentInput[]>()
  for (const d of departments) {
    if (d.department._id === rootId) continue
    const parent =
      d.department.parent !== undefined && d.department.parent !== d.department._id && byId.has(d.department.parent)
        ? d.department.parent
        : rootId
    const list = childrenOf.get(parent) ?? []
    list.push(d)
    childrenOf.set(parent, list)
  }

  const visited = new Set<Ref<Department>>()
  function node (d: DepartmentInput, title: string): OrgNode {
    visited.add(d.department._id)
    const members = membersOf.get(d.department._id) ?? []
    const children = (childrenOf.get(d.department._id) ?? [])
      .filter((c) => !visited.has(c.department._id))
      .sort((a, b) => a.department.name.localeCompare(b.department.name))
      .map((c) => node(c, c.department.name))
    return {
      id: d.department._id,
      kind: 'department',
      title,
      person: d.lead,
      leadMissing: d.lead === undefined && d.department._id !== rootId,
      members,
      memberCount: members.length,
      children,
      department: d.department
    }
  }

  const root = byId.get(rootId)
  if (root === undefined) return []
  return [node(root, rootTitle)]
}

export interface FileInput {
  file: PersonnelFile
  person: Person
}

/**
 * Reporting lines from the manager of each personnel file. Files without a (known) manager are roots.
 */
export function buildReportingTree (files: FileInput[]): OrgNode[] {
  const active = files.filter((f) => f.file.status !== PersonnelStatus.Left)
  const byPerson = new Map(active.map((f) => [f.file.person, f]))
  const childrenOf = new Map<Ref<Person>, FileInput[]>()
  const roots: FileInput[] = []
  for (const f of active) {
    const manager = f.file.manager
    if (manager != null && manager !== f.file.person && byPerson.has(manager)) {
      const list = childrenOf.get(manager) ?? []
      list.push(f)
      childrenOf.set(manager, list)
    } else {
      roots.push(f)
    }
  }

  const visited = new Set<Ref<Person>>()
  function node (f: FileInput): OrgNode {
    visited.add(f.file.person)
    const children = (childrenOf.get(f.file.person) ?? [])
      .filter((c) => !visited.has(c.file.person))
      .sort((a, b) => a.person.name.localeCompare(b.person.name))
      .map(node)
    return {
      id: f.file._id,
      kind: 'person',
      title: f.person.name,
      subtitle: f.file.position,
      person: f.person,
      muted: f.file.status === PersonnelStatus.Preboarding,
      children,
      file: f.file
    }
  }

  // Roots with reports first, so that the chart starts with the management line
  const sorted = roots.sort(
    (a, b) => (childrenOf.get(b.file.person)?.length ?? 0) - (childrenOf.get(a.file.person)?.length ?? 0)
  )
  const result = sorted.map(node)
  // Members of a reporting cycle have no root; show them as separate roots
  for (const f of active) {
    if (!visited.has(f.file.person)) result.push(node(f))
  }
  return result
}

/**
 * Department members for the HR team: the staff of the absence module plus everyone who only has a personnel file.
 * Files without a department belong to the root. People who already are staff keep their staff department.
 */
export function mergeStaffWithFiles (rootId: Ref<Department>, staff: StaffInput[], files: FileInput[]): StaffInput[] {
  const known = new Set(staff.map((s) => s.person._id))
  const result = [...staff]
  for (const f of files) {
    if (f.file.status === PersonnelStatus.Left || known.has(f.person._id)) continue
    known.add(f.person._id)
    result.push({ person: f.person, department: f.file.department ?? rootId })
  }
  return result
}

/**
 * Fallback lead of a department: the manager inside the department who has the most direct reports there.
 */
export function inferTeamLeads (files: FileInput[]): Map<Ref<Department>, Person> {
  const active = files.filter((f) => f.file.status !== PersonnelStatus.Left && f.file.department != null)
  const byPerson = new Map(active.map((f) => [f.file.person, f]))
  const counts = new Map<Ref<Department>, Map<Ref<Person>, number>>()
  for (const f of active) {
    const manager = f.file.manager != null ? byPerson.get(f.file.manager) : undefined
    if (manager === undefined || manager.file.department !== f.file.department) continue
    const department = f.file.department as Ref<Department>
    const perManager = counts.get(department) ?? new Map<Ref<Person>, number>()
    perManager.set(manager.file.person, (perManager.get(manager.file.person) ?? 0) + 1)
    counts.set(department, perManager)
  }

  const result = new Map<Ref<Department>, Person>()
  for (const [department, perManager] of counts) {
    const [top] = [...perManager.entries()].sort((a, b) => b[1] - a[1])
    const lead = byPerson.get(top[0])
    if (lead !== undefined) result.set(department, lead.person)
  }
  return result
}
